import { Inject, Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, map } from 'rxjs';

import { ApiUrlService } from '../core/api-url';
import {
  TenantBootstrapConfig,
  TenantBootstrapState,
  TenantConfig,
} from './tenant.model';
import { TENANT_RUNTIME_CONFIG, tenantConfigMatchesRuntime, TenantRuntimeConfig } from './tenant-runtime-config';

const INITIAL_STATE: TenantBootstrapState = {
  status: 'idle',
  config: null,
  error: null,
};

@Injectable({ providedIn: 'root' })
export class TenantBootstrapService {
  private readonly http: HttpClient;
  private bootstrapPromise: Promise<TenantBootstrapConfig | null> | null = null;
  private readonly stateSubject = new BehaviorSubject<TenantBootstrapState>(INITIAL_STATE);

  readonly state$ = this.stateSubject.asObservable();
  readonly config$ = this.state$.pipe(map((state) => state.config));
  readonly tenant$ = this.config$.pipe(map((config) => config?.tenant ?? null));

  constructor(
    httpBackend: HttpBackend,
    private apiUrl: ApiUrlService,
    @Inject(TENANT_RUNTIME_CONFIG) private runtime: TenantRuntimeConfig,
  ) {
    this.http = new HttpClient(httpBackend);
  }

  get currentState(): TenantBootstrapState {
    return this.stateSubject.value;
  }

  get currentConfig(): TenantBootstrapConfig | null {
    return this.currentState.config;
  }

  get currentTenant(): TenantConfig | null {
    return this.currentConfig?.tenant ?? null;
  }

  applyConfig(config: TenantBootstrapConfig): void {
    this.stateSubject.next({ status: 'ready', config, error: null });
  }

  init(): Promise<TenantBootstrapConfig | null> {
    if (this.bootstrapPromise) return this.bootstrapPromise;

    this.stateSubject.next({ status: 'loading', config: null, error: null });
    this.bootstrapPromise = firstValueFrom(
      this.http.get<TenantBootstrapConfig>(this.apiUrl.url('/tenant/config'), {
        withCredentials: true,
      })
    )
      .then((config) => {
        if (String(config?.tenant?.status || '').toLowerCase() !== 'active') {
          throw new Error('This league is not currently active.');
        }
        if (!tenantConfigMatchesRuntime(config?.tenant?.slug, this.runtime)) {
          throw new Error(`The returned league does not match ${this.runtime.displayName}.`);
        }
        this.stateSubject.next({ status: 'ready', config, error: null });
        return config;
      })
      .catch((error) => {
        this.stateSubject.next({ status: 'error', config: null, error });
        return null;
      });

    return this.bootstrapPromise;
  }

  retry(): Promise<TenantBootstrapConfig | null> {
    this.bootstrapPromise = null;
    return this.init();
  }
}
